const UPDATES_WS_PATH = '/api/updates/ws';
const HEARTBEAT_UPDATE_TYPE = 'heartbeat';

/** Attempts made before the connection is reported as `degraded`; retries continue past this. */
const MAX_FAST_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
/** Generous relative to the 25s server heartbeat; `onclose` is the primary failure signal. */
const STALE_CONNECTION_TIMEOUT_MS = 70000;
/**
 * A handshake that never completes produces neither `onopen` nor `onclose`, so nothing else in
 * this class would notice it. Without this the socket sits in `connecting` indefinitely.
 */
const CONNECT_TIMEOUT_MS = 15000;
/** Server-side viewer records expire after 20s, so presence has to be restated inside that. */
const PRESENCE_REFRESH_MS = 15000;

/** Application close codes sent by the server. 4001 means the per-user session cap evicted us. */
const CLOSE_SESSION_CAP_REACHED = 4001;
const CLOSE_POLICY_VIOLATION = 1008;

/**
 * UpdateSocketClient - transport for real-time updates.
 *
 * Same public surface as the SSE client it replaces (connect / reconnect / disconnect /
 * getConnectionState). The server sends every update for the clinic and callers decide what to act
 * on; the only thing sent upstream is presence, which SSE could not do without an HTTP request.
 */
export class UpdateSocketClient {
  constructor(apiClient) {
    this.apiClient = apiClient;
    this.socket = null;
    this.onMessage = null;
    this.onReconnect = null;
    this.onStateChange = null;

    this.connectionState = 'disconnected';

    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.staleTimer = null;
    this.presenceTimer = null;
    this.connectTimer = null;
    // When the current handshake started, so a wedged one can be recognised even if the timer
    // above was throttled by a background tab.
    this.connectingSince = null;

    // Set by consumers and replayed after every successful (re)connect.
    this.viewing = null;

    // True once the caller has asked for a connection; cleared by disconnect().
    this.wanted = false;
    // True after at least one connection has been established, so reconnects can trigger catch-up.
    this.hasConnectedBefore = false;
  }

  /**
   * @param {Function} onMessage - receives each non-heartbeat update
   * @param {Function} [onReconnect] - called after a reconnect so callers can catch up on
   *                                   anything missed while the socket was down
   * @param {Function} [onStateChange] - receives the new connection state on every transition
   */
  connect(onMessage, onReconnect, onStateChange) {
    if (onMessage) this.onMessage = onMessage;
    if (onReconnect) this.onReconnect = onReconnect;
    if (onStateChange) this.onStateChange = onStateChange;

    if (this.connectionState === 'connected') {
      return;
    }

    // A handshake still in progress is left alone, but one that has stalled past the timeout is
    // replaced. Returning early in that case is what previously made a wedged socket permanent.
    if (this.connectionState === 'connecting') {
      if (!this.isHandshakeStalled()) {
        return;
      }
      this._teardownSocket();
    }

    this.wanted = true;
    // A retry may already be queued from an earlier failure; opening now without cancelling it
    // would leave two sockets racing.
    this._cancelReconnectTimer();
    this._open();
  }

  /** Force a fresh connection, e.g. after a clinic change. */
  reconnect(onMessage, onReconnect, onStateChange) {
    if (onMessage) this.onMessage = onMessage;
    if (onReconnect) this.onReconnect = onReconnect;
    if (onStateChange) this.onStateChange = onStateChange;

    this.wanted = true;
    this._teardownSocket();
    this._cancelReconnectTimer();
    this._clearStaleTimer();
    this._setConnectionState('disconnected');
    this.reconnectAttempts = 0;
    this._open();
  }

  disconnect() {
    this.wanted = false;
    this._teardownSocket();
    this._cancelReconnectTimer();
    this._clearStaleTimer();
    this._clearPresenceTimer();
    this._setConnectionState('disconnected');
    this.reconnectAttempts = 0;
    this.connectingSince = null;
    this.viewing = null;
  }

  getConnectionState() {
    return this.connectionState;
  }

  isConnected() {
    return this.connectionState === 'connected';
  }

  /** True when a handshake has been open longer than a handshake should ever take. */
  isHandshakeStalled() {
    return this.connectionState === 'connecting'
      && this.connectingSince != null
      && Date.now() - this.connectingSince >= CONNECT_TIMEOUT_MS;
  }

  /**
   * Report that this user is reading an item. Replaces the old 15s `/viewed` POST: same cadence,
   * but a socket frame instead of a request that occupies a server thread.
   * @param {{scope: string, itemId: string}|null} viewing - null clears presence
   */
  setViewing(viewing) {
    this._clearPresenceTimer();
    this.viewing = viewing || null;
    if (!this.viewing) {
      return;
    }

    this._sendViewing();
    this.presenceTimer = setInterval(() => this._sendViewing(), PRESENCE_REFRESH_MS);
  }

  _sendViewing() {
    if (this.viewing) {
      this._send({ type: 'viewing', scope: this.viewing.scope, itemId: this.viewing.itemId });
    }
  }

  _clearPresenceTimer() {
    if (this.presenceTimer) {
      clearInterval(this.presenceTimer);
      this.presenceTimer = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  _open() {
    const url = this._buildUrl();
    if (!url) {
      console.error('[UpdateSocketClient] Cannot connect - base URL not available');
      this._setConnectionState('error');
      this._scheduleReconnect();
      return;
    }

    this.connectingSince = Date.now();
    this._setConnectionState('connecting');

    let socket;
    try {
      socket = new WebSocket(url);
    } catch (error) {
      console.error('[UpdateSocketClient] Failed to create socket:', error);
      this.connectingSince = null;
      this._setConnectionState('error');
      this._scheduleReconnect();
      return;
    }

    socket.onopen = () => {
      this._clearConnectTimer();
      this.connectingSince = null;
      this._setConnectionState('connected');
      this.reconnectAttempts = 0;
      this._resetStaleTimer();

      // The server holds no state for us across connections, so restate what we are reading.
      this._sendViewing();

      if (this.hasConnectedBefore && this.onReconnect) {
        try {
          this.onReconnect();
        } catch (error) {
          console.error('[UpdateSocketClient] Reconnect handler failed:', error);
        }
      }
      this.hasConnectedBefore = true;
    };

    socket.onmessage = (event) => {
      this._resetStaleTimer();

      let update;
      try {
        update = JSON.parse(event.data);
      } catch (error) {
        console.warn('[UpdateSocketClient] Discarded unparseable message');
        return;
      }

      if (update.updateType === HEARTBEAT_UPDATE_TYPE) {
        return;
      }

      if (this.onMessage) {
        this.onMessage(update);
      }
    };

    socket.onclose = (event) => {
      if (this.socket !== socket) {
        return; // superseded by a newer socket
      }
      this.socket = null;
      this._clearStaleTimer();
      this._clearConnectTimer();
      this.connectingSince = null;

      if (!this.wanted) {
        this._setConnectionState('disconnected');
        return;
      }

      if (event.code === CLOSE_SESSION_CAP_REACHED || event.code === CLOSE_POLICY_VIOLATION) {
        console.warn(`[UpdateSocketClient] Server declined this connection (code ${event.code}); not retrying`);
        this._setConnectionState('failed');
        return;
      }

      this._setConnectionState('error');
      this._scheduleReconnect();
    };

    socket.onerror = () => {
      // Always followed by onclose, which owns the reconnect decision.
      console.warn('[UpdateSocketClient] Socket error');
    };

    this.socket = socket;

    // Neither `onopen` nor `onclose` fires for a handshake that hangs, so this is the only thing
    // that can free it. The stale timer cannot help: it is only armed once a socket is open.
    this._clearConnectTimer();
    this.connectTimer = setTimeout(() => {
      this.connectTimer = null;
      if (this.socket !== socket) {
        return; // already replaced
      }
      console.warn(`[UpdateSocketClient] Handshake did not complete within ${CONNECT_TIMEOUT_MS}ms`);
      this._teardownSocket();
      this.connectingSince = null;
      this._setConnectionState('error');
      this._scheduleReconnect();
    }, CONNECT_TIMEOUT_MS);
  }

  _buildUrl() {
    const baseUrl = this.apiClient?.baseUrl;
    if (!baseUrl) {
      return null;
    }

    const wsBase = baseUrl.replace(/^http/, 'ws').replace(/\/$/, '');
    // Handshakes cannot carry custom headers, so the clinic travels as a query param.
    const clinicId = sessionStorage.getItem('selectedClinicId');
    const query = clinicId ? `?clinicId=${encodeURIComponent(clinicId)}` : '';
    return `${wsBase}${UPDATES_WS_PATH}${query}`;
  }

  _send(payload) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    try {
      this.socket.send(JSON.stringify(payload));
    } catch (error) {
      console.warn('[UpdateSocketClient] Failed to send message:', error);
    }
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) {
      return;
    }

    const backoff = Math.min(
      BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts),
      MAX_RECONNECT_DELAY_MS
    );
    // Jitter so a server restart does not bring every tab back in the same instant.
    const delay = backoff / 2 + Math.random() * (backoff / 2);

    this.reconnectAttempts++;

    // Retries continue for as long as the caller wants a connection. Giving up after a fixed
    // number of attempts meant an outage longer than the backoff budget left the tab silent
    // until a full reload; instead, report `degraded` and keep trying at the capped delay.
    if (this.reconnectAttempts > MAX_FAST_RECONNECT_ATTEMPTS) {
      console.warn(`[UpdateSocketClient] Still down after ${MAX_FAST_RECONNECT_ATTEMPTS} attempts; retrying every ${MAX_RECONNECT_DELAY_MS}ms`);
      this._setConnectionState('degraded');
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.wanted) {
        this._open();
      }
    }, delay);
  }

  _cancelReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  _setConnectionState(nextState) {
    if (this.connectionState === nextState) {
      return;
    }
    this.connectionState = nextState;
    if (this.onStateChange) {
      try {
        this.onStateChange(nextState);
      } catch (error) {
        console.error('[UpdateSocketClient] State change handler failed:', error);
      }
    }
  }

  _clearConnectTimer() {
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  /** Backstop for a network that dies without the browser noticing. */
  _resetStaleTimer() {
    this._clearStaleTimer();
    this.staleTimer = setTimeout(() => {
      console.warn('[UpdateSocketClient] No traffic in 70s - treating connection as stale');
      this._teardownSocket();
      this._setConnectionState('error');
      this._scheduleReconnect();
    }, STALE_CONNECTION_TIMEOUT_MS);
  }

  _clearStaleTimer() {
    if (this.staleTimer) {
      clearTimeout(this.staleTimer);
      this.staleTimer = null;
    }
  }

  _teardownSocket() {
    this._clearConnectTimer();
    if (!this.socket) {
      return;
    }
    const socket = this.socket;
    this.socket = null;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    try {
      socket.close();
    } catch (error) {
      // already closing
    }
  }
}
